/* FVS Step 10 and Step 19 */
/* eslint-disable operator-linebreak */
import { ReactElement, useEffect } from 'react';
import { tile, iFVSInfoDisplay } from '@interfaces/interfaces';
import { Field, useFormikContext, getIn } from 'formik';
import { iFVSFormValues } from '@services/FVSService';
import { TileRadio, Alert, TextInput, DateInput, LicenceField, LimitedTextArea } from '@components';
import FVSStep10AlertContent1 from '@components/Content/AlertContent/FVSStep10AlertContent1';
import FVSStep10AlertContent2 from '@components/Content/AlertContent/FVSStep10AlertContent2';
import FVSStep19AlertContent2 from '@components/Content/AlertContent/FVSStep19AlertContent2';
import gridStyles from '@pages/forms/Grid.module.scss';
import classnames from 'classnames';
import styles from './LikelyPossibilities.module.scss';
import InfoIcon from '../../images/info.svg';

interface iLikelyPossibilities {
  displayObject: iFVSInfoDisplay;
  currentStepNo: string;
}

function LikelyPossibilities({ displayObject, currentStepNo }: iLikelyPossibilities): ReactElement {
  const FinesWithdrawn = (): ReactElement => (
    <>
      <div>Have the fine(s) withdrawn</div>
    </>
  );

  const NominatedDriver = (): ReactElement => (
    <>
      <div>Nominate the driver who was in control of the vehicle at the time of the offence(s) - if safe to do so</div>
    </>
  );

  const Combination = (): ReactElement => (
    <>
      <div>A combination of the above</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <FinesWithdrawn />,
      value: 'FinesWithdrawn',
    },
    {
      body: <NominatedDriver />,
      value: 'DriverNominated',
    },
    {
      body: <Combination />,
      value: 'AboveCombination',
    },
  ];

  const { values, errors, touched }: { values: iFVSFormValues; errors: any; touched: any } = useFormikContext();
  const formikContext = useFormikContext();
  const { WhatWouldYouLikeToHappen } = values;
  const { pageTitle } = displayObject.pages.likelyPossibilities[currentStepNo];
  const {
    likelyPossibility,
    givenNameOptional,
    familyNameOptional,
    dateOfBirthOptional,
    driverLicenceOptional,
    licenceStateOptional,
    stateCountryOfIssueOptional,
    noDriverLicenceOptional,
    howDealtWithFines,
  } = displayObject.pages.likelyPossibilities[currentStepNo].fields;
  const hasError = getIn(errors, likelyPossibility.formikName);
  const touchedError = getIn(touched, likelyPossibility.formikName);
  useEffect(() => {
    if (WhatWouldYouLikeToHappen === 'AboveCombination' || WhatWouldYouLikeToHappen === 'FinesWithdrawn') {
      formikContext.setFieldValue('DOBOptional', '');
      formikContext.setFieldTouched('DOBOptional');
    }
  }, [values.DOBOptional, WhatWouldYouLikeToHappen]);

  return (
    <div className={gridStyles.form}>
      <div className="vfH2">{pageTitle}</div>
      <div
        className={classnames({
          vfH3: true,
          [styles.titleError]: touchedError && hasError,
        })}>
        Tell us what you would like to happen, if your application is successful. Note: what you tell us here will not impact the success of the application.
      </div>
      <div className={gridStyles.gridColFull}>
        <Field name={likelyPossibility.formikName} component={TileRadio} tileData={TILE_DATA} classname={styles.tile} />
        {WhatWouldYouLikeToHappen === 'FinesWithdrawn' && <Alert message={<FVSStep10AlertContent1 />} theme="info" icon={InfoIcon} />}
        {WhatWouldYouLikeToHappen === 'DriverNominated' &&
          (currentStepNo === 'step10' ? (
            <Alert message={<FVSStep10AlertContent2 />} theme="info" icon={InfoIcon} />
          ) : (
            <Alert message={<FVSStep19AlertContent2 />} theme="info" icon={InfoIcon} />
          ))}
        {WhatWouldYouLikeToHappen === 'DriverNominated' && (
          <div>
            <Field name={givenNameOptional.formikName} component={TextInput} title={givenNameOptional.title} />
            <Field name={familyNameOptional.formikName} component={TextInput} title={familyNameOptional.title} />
            <Field name={dateOfBirthOptional.formikName} component={DateInput} title={dateOfBirthOptional.title} minDate={new Date(1900, 1, 1)} maxDate={new Date()} />
            <LicenceField
              name={driverLicenceOptional.formikName}
              title={driverLicenceOptional.title}
              nameStateOfIssue={licenceStateOptional.formikName}
              nameCountryOfIssue={stateCountryOfIssueOptional.formikName}
              nameNoDriverLicence={noDriverLicenceOptional.formikName}
              noDriverLicenceOptRequired={false}
            />
          </div>
        )}
        {WhatWouldYouLikeToHappen === 'AboveCombination' && (
          <Field component={LimitedTextArea} name={howDealtWithFines.formikName} limit={3000} title={howDealtWithFines.title} isMandatory />
        )}
        {WhatWouldYouLikeToHappen === 'AboveCombination' && <Alert message="Somebody from Fines Victoria will be in contact to discuss" theme="info" icon={InfoIcon} />}
      </div>
    </div>
  );
}

export default LikelyPossibilities;
